import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Select, message, Card, Typography, Button, Space, Modal, Spin, Row, Col, Form, Input, Drawer } from 'antd';
import { FolderOutlined, FileOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import TestItemList from '../components/testItems/TestItemList';
import { FolderForm } from '../components/testItems/FolderForm';
import TestDataForm from '../components/testItems/TestDataForm';
import projectService from '../services/projectService';
import { Project } from '../types/project';
import { TestItem, Folder, TestItemCreate, TestData } from '../types/testItem';
import { testItemService } from '../services/testItemService';
import { TestItemForm } from '../components/testItems/TestItemForm';
import { useAuth } from '../contexts/AuthContext';
import { TestDataList } from '../components/testItems/TestDataList';

const { Title } = Typography;
const { Option } = Select;

const TestCasesPage: React.FC = () => {
    const { t } = useTranslation();
    const { projectId: urlProjectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [projects, setProjects] = useState<Project[]>([]);
    const [selectedProject, setSelectedProject] = useState<number | null>(
        urlProjectId ? parseInt(urlProjectId) : null
    );
    const [selectedProjectTitle, setSelectedProjectTitle] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [testItems, setTestItems] = useState<TestItem[]>([]);
    const [itemsLoading, setItemsLoading] = useState(false);
    const [isFolderFormVisible, setIsFolderFormVisible] = useState(false);
    const [editingFolder, setEditingFolder] = useState<Folder | null>(null);
    const [folders, setFolders] = useState<Folder[]>([]);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [editingItem, setEditingItem] = useState<TestItem | null>(null);
    const [filteredItems, setFilteredItems] = useState<TestItem[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [form] = Form.useForm<TestItemCreate>();
    const [isTestDataFormVisible, setIsTestDataFormVisible] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined);
    const [isTestDataListVisible, setIsTestDataListVisible] = useState(false);
    const [testData, setTestData] = useState<TestData[]>([]);

    useEffect(() => {
        fetchUserProjects();
    }, []);

    const fetchUserProjects = async () => {
        try {
            setLoading(true);
            const data = await projectService.getUserProjects();
            if (Array.isArray(data)) {
                setProjects(data);
            } else {
                console.error('Received non-array data from getUserProjects:', data);
                setProjects([]);
                message.error(t('projects.fetchError'));
            }
        } catch (error) {
            console.error('Error fetching projects:', error);
            setProjects([]);
            message.error(t('projects.fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const fetchProjectDetails = useCallback(async () => {
        if (!selectedProject) return;
        try {
            const project = await projectService.getProject(selectedProject);
            if (project) {
                setSelectedProjectTitle(project.name);
            } else {
                message.error(t('projects.projectNotFound'));
                setSelectedProject(null);
                navigate('/test-cases');
            }
        } catch (error) {
            console.error('Error fetching project details:', error);
            message.error(t('projects.fetchError'));
            setSelectedProject(null);
            navigate('/test-cases');
        }
    }, [selectedProject, t, navigate]);

    const fetchData = useCallback(async () => {
        if (!selectedProject) return;
        
        setItemsLoading(true);
        try {
            const [items, folderList] = await Promise.all([
                testItemService.getTestItems({ project_id: selectedProject }),
                testItemService.getFolders(selectedProject)
            ]);
            setTestItems(Array.isArray(items) ? items : []);
            setFolders(Array.isArray(folderList) ? folderList : []);
            setFilteredItems(Array.isArray(items) ? items : []);
        } catch (error) {
            console.error('Error fetching test items:', error);
            message.error(t('testItems.errors.loadFailed'));
        } finally {
            setItemsLoading(false);
        }
    }, [selectedProject, t]);

    useEffect(() => {
        if (selectedProject) {
            fetchData();
            fetchProjectDetails();
        }
    }, [selectedProject, fetchData, fetchProjectDetails]);

    useEffect(() => {
        if (isTestDataListVisible) {
            const fetchTestData = async () => {
                try {
                    const data = await testItemService.getTestData(selectedProject || 0);
                    setTestData(data);
                } catch (error) {
                    message.error('Не удалось загрузить тестовые данные');
                }
            };
            fetchTestData();
        }
    }, [isTestDataListVisible, selectedProject]);

    const handleProjectChange = useCallback((projectId: number) => {
        setSelectedProject(projectId);
        const project = projects.find(p => p.id === projectId);
        if (project) {
            setSelectedProjectTitle(project.name);
            navigate(`/test-cases/${projectId}`);
        } else {
            message.error(t('projects.projectNotFound'));
            setSelectedProject(null);
            navigate('/test-cases');
        }
    }, [projects, navigate, t]);

    const handleCreateFolder = useCallback(() => {
        setEditingFolder(null);
        setIsFolderFormVisible(true);
    }, []);

    const handleEditFolder = useCallback((folder: Folder) => {
        setEditingFolder(folder);
        setIsFolderFormVisible(true);
    }, []);

    const handleDeleteFolder = async (folder: Folder) => {
        if (!selectedProject) return;

        Modal.confirm({
            title: t('testItems.confirmDeleteFolder'),
            content: t('testItems.confirmDeleteFolderMessage', { name: folder.name }),
            okText: t('common.yes'),
            okType: 'danger',
            cancelText: t('common.no'),
            onOk: async () => {
                try {
                    // Get all test items in this folder and subfolders
                    const allTestItems = testItems.filter(item => item.folder_id === folder.id);
                    const subfolders = folders.filter(f => f.parent_id === folder.id);

                    // Delete all test items in this folder
                    for (const item of allTestItems) {
                        await testItemService.deleteTestItem(selectedProject, item.id);
                    }

                    // Delete all subfolders recursively
                    for (const subfolder of subfolders) {
                        await handleDeleteFolder(subfolder);
                    }

                    // Delete the folder itself
                    await testItemService.deleteFolder(selectedProject, folder.id);
                    message.success(t('testItems.folderDeleted'));
                    fetchData();
                } catch (error) {
                    console.error('Error deleting folder:', error);
                    message.error(t('testItems.folderDeleteError'));
                }
            }
        });
    };

    const handleFolderFormSubmit = useCallback(async (values: any) => {
        if (!selectedProject) return;
        try {
            if (editingFolder) {
                await testItemService.updateFolder(selectedProject, editingFolder.id, values);
                message.success(t('testItems.messages.updateFolderSuccess'));
            } else {
                await testItemService.createFolder(selectedProject, values);
                message.success(t('testItems.messages.createFolderSuccess'));
            }
            setIsFolderFormVisible(false);
            fetchData();
        } catch (error) {
            message.error(t('testItems.errors.saveFolderFailed'));
        }
    }, [selectedProject, editingFolder, fetchData, t]);

    const handleEditItem = useCallback((item: TestItem) => {
        setEditingItem(item);
        setIsFormVisible(true);
    }, []);

    const fetchTestItems = useCallback(async () => {
        if (!selectedProject) return;
        
        try {
            setLoading(true);
            const items = await testItemService.getTestItems({
                project_id: selectedProject
            });
            const itemsArray = Array.isArray(items) ? items : [];
            setTestItems(itemsArray);
            setFilteredItems(itemsArray);
        } catch (error) {
            console.error('Error fetching test items:', error);
            message.error(t('testItems.errors.fetchFailed'));
        } finally {
            setLoading(false);
        }
    }, [selectedProject, t]);

    useEffect(() => {
        fetchTestItems();
    }, [fetchTestItems]);

    const handleSubmit = useCallback(async (values: any) => {
        if (!selectedProject) return;

        try {
            let updatedItem: TestItem;
            if (editingItem) {
                updatedItem = await testItemService.updateTestItem(editingItem.id, values);
                message.success(t('testItems.messages.updateSuccess'));
            } else {
                updatedItem = await testItemService.createTestItem({
                    ...values,
                    project_id: selectedProject
                });
                message.success(t('testItems.messages.createSuccess'));
            }
            
            // Force a refresh of all data
            await fetchData();
            
            setEditingItem(null);
            setIsFormVisible(false);
        } catch (error) {
            console.error('Error saving test item:', error);
            message.error(t('testItems.errors.saveFailed'));
        }
    }, [selectedProject, editingItem, t, fetchData]);

    const handleCreateItem = useCallback(() => {
        setEditingItem(null);
        setIsFormVisible(true);
    }, []);

    const handleDeleteItem = async (item: TestItem) => {
        Modal.confirm({
            title: t('testItems.confirmDelete'),
            content: t('testItems.confirmDeleteMessage', { name: item.title }),
            okText: t('common.yes'),
            okType: 'danger',
            cancelText: t('common.no'),
            onOk: async () => {
                try {
                    await testItemService.deleteTestItem(selectedProject || 0, item.id);
                    setTestItems(prevItems => prevItems.filter(i => i.id !== item.id));
                    setFilteredItems(prevItems => prevItems.filter(i => i.id !== item.id));
                    message.success(t('testItems.messages.deleteSuccess'));
                } catch (error) {
                    console.error('Error deleting test item:', error);
                    message.error(t('testItems.errors.deleteFailed'));
                }
            }
        });
    };

    const handleCreateTestData = () => {
        setIsTestDataListVisible(true);
    };

    const handleTestDataSuccess = () => {
        setIsTestDataFormVisible(false);
        // No need to fetch test items here since we're not showing test data in this view
    };

    const handleAddTestDataToFolder = async (data: TestData, folderId: number) => {
        try {
            await testItemService.updateTestData(selectedProject || 0, data.id, { 
                project_id: data.project_id,
                name: data.name,
                description: data.description,
                data: data.data,
                attachment_ids: data.attachment_ids,
                tags: data.tags,
                folder_id: folderId 
            });
            message.success('Тестовые данные добавлены к папке');
        } catch (error) {
            message.error('Не удалось добавить тестовые данные к папке');
        }
    };

    const handleUpdateTestData = async (data: TestData) => {
        try {
            await testItemService.updateTestData(selectedProject || 0, data.id, data);
            message.success('Тестовые данные обновлены');
            fetchData();
        } catch (error) {
            message.error('Не удалось обновить тестовые данные');
        }
    };

    const handleDeleteTestData = async (dataId: number) => {
        try {
            await testItemService.deleteTestData(selectedProject || 0, dataId);
            message.success('Тестовые данные удалены');
            fetchData();
        } catch (error) {
            message.error('Не удалось удалить тестовые данные');
        }
    };

    const handleCreateTestDataItem = async (data: Omit<TestData, 'id'>) => {
        try {
            await testItemService.createTestData(selectedProject || 0, data);
            message.success('Тестовые данные созданы');
            fetchData();
        } catch (error) {
            message.error('Не удалось создать тестовые данные');
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: '24px' }}>
            <Row gutter={[24, 24]}>
                <Col span={24}>
                    <Card>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <Title level={2}>{t('testItems.title')}</Title>
                            <Select
                                style={{ width: 300 }}
                                placeholder={t('testItems.selectProject')}
                                value={selectedProject}
                                onChange={handleProjectChange}
                                loading={loading}
                            >
                                {projects.map(project => (
                                    <Option key={project.id} value={project.id}>
                                        {project.name}
                                    </Option>
                                ))}
                            </Select>
                        </Space>
                    </Card>
                </Col>

                {selectedProject && (
                    <>
                        <Col span={24}>
                            <Card>
                                <Space direction="vertical" style={{ width: '100%' }}>
                                    <Title level={3}>{selectedProjectTitle}</Title>
                                    <Space>
                                        <Button
                                            type="primary"
                                            icon={<FolderOutlined />}
                                            onClick={handleCreateFolder}
                                        >
                                            {t('testItems.createFolder')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<FileOutlined />}
                                            onClick={handleCreateItem}
                                        >
                                            {t('testItems.createItem')}
                                        </Button>
                                        <Button
                                            type="primary"
                                            icon={<FileOutlined />}
                                            onClick={handleCreateTestData}
                                        >
                                            {t('testItems.createTestData')}
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        </Col>

                        <Col span={24}>
                            <Card loading={itemsLoading}>
                                <TestItemList
                                    projectId={selectedProject}
                                    items={filteredItems}
                                    folders={folders}
                                    loading={itemsLoading}
                                    onEdit={handleEditItem}
                                    onDelete={handleDeleteItem}
                                    onEditFolder={handleEditFolder}
                                    onDeleteFolder={handleDeleteFolder}
                                    onDataChange={fetchData}
                                />
                            </Card>
                        </Col>
                    </>
                )}
            </Row>

            <Modal
                title={editingFolder ? t('testItems.editFolder') : t('testItems.createFolder')}
                open={isFolderFormVisible}
                onCancel={() => setIsFolderFormVisible(false)}
                footer={null}
                destroyOnHidden
            >
                <FolderForm
                    projectId={selectedProject || 0}
                    onSubmit={handleFolderFormSubmit}
                    onCancel={() => setIsFolderFormVisible(false)}
                    initialValues={editingFolder}
                    folders={folders}
                />
            </Modal>

            <Modal
                title={editingItem ? t('testItems.editItem') : t('testItems.createItem')}
                open={isFormVisible}
                onCancel={() => {
                    setIsFormVisible(false);
                    setEditingItem(null);
                }}
                footer={null}
                width={800}
                destroyOnHidden
            >
                <TestItemForm
                    projectId={selectedProject || 0}
                    onSubmit={handleSubmit}
                    onCancel={() => {
                        setIsFormVisible(false);
                        setEditingItem(null);
                    }}
                    initialValues={editingItem || undefined}
                    folders={folders}
                />
            </Modal>

            <TestDataForm
                projectId={selectedProject || 0}
                visible={isTestDataFormVisible}
                onCancel={() => setIsTestDataFormVisible(false)}
                onSuccess={handleTestDataSuccess}
            />

            <Drawer
                title="Тестовые данные"
                placement="right"
                width={720}
                onClose={() => setIsTestDataListVisible(false)}
                open={isTestDataListVisible}
            >
                <TestDataList
                    testItemId={selectedProject || 0}
                    folderId={selectedFolderId}
                    onDataChange={handleTestDataSuccess}
                />
            </Drawer>
        </div>
    );
};

export default TestCasesPage; 